import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CargaInicialBody, RelatorioCarga } from '@pcp/contracts';
import { useCargaInicial } from '../../api/embarques';
import { ApiRequestError } from '../../api/client';
import { t } from '../../i18n/pt-BR';
import { fileToRecords } from '../../lib/spreadsheet';

const c = t.shipments.load;

/** Carga inicial da planilha FOLLOW UP: simula, mostra o relatório de rejeições e só então importa. */
export function CargaInicialPage() {
  const [linhas, setLinhas] = useState<CargaInicialBody['linhas']>([]);
  const [nome, setNome] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [relatorio, setRelatorio] = useState<RelatorioCarga | null>(null);
  const carga = useCargaInicial();

  const onFile = async (file: File | undefined) => {
    setErro(null);
    setRelatorio(null);
    if (!file) return;
    try {
      const rows = await fileToRecords(file);
      if (!rows.length) throw new Error('A planilha não tem linhas de dados.');
      setLinhas(rows);
      setNome(file.name);
    } catch (e) {
      setLinhas([]);
      setErro(e instanceof Error ? e.message : t.states.datasulDown);
    }
  };

  const rodar = async (simular: boolean) => {
    setErro(null);
    try {
      setRelatorio(await carga.mutateAsync({ body: { linhas }, simular }));
    } catch (e) {
      setErro(e instanceof ApiRequestError ? e.message : t.states.datasulDown);
    }
  };

  return (
    <section className="panel" aria-labelledby="carga-title">
      <p>
        <Link to="/importacao">{t.shipments.form.back}</Link>
      </p>
      <h2 id="carga-title">{c.title}</h2>
      <p className="hint">{c.hint}</p>

      <div className="field" style={{ maxWidth: 520 }}>
        <label htmlFor="carga-file">{c.file}</label>
        <input id="carga-file" type="file" accept=".xlsx" onChange={(e) => void onFile(e.target.files?.[0])} />
        {nome ? (
          <span className="hint">
            {nome} · {linhas.length} {c.read}
          </span>
        ) : null}
      </div>
      {erro ? (
        <div className="form-error" role="alert">
          {erro}
        </div>
      ) : null}

      <div className="row" style={{ marginTop: 12 }}>
        <button type="button" className="btn" disabled={!linhas.length || carga.isPending} onClick={() => void rodar(true)}>
          {c.simulate}
        </button>
        <button
          type="button"
          className="btn ghost"
          disabled={!relatorio?.simulacao || relatorio.importadas === 0 || carga.isPending}
          onClick={() => void rodar(false)}
        >
          {c.run}
        </button>
      </div>

      {relatorio ? (
        <div style={{ marginTop: 16 }} role="status">
          <div className={`banner ${relatorio.simulacao ? 'info' : 'ok'}`}>
            {relatorio.simulacao ? c.simulation : c.done}
          </div>
          <ul>
            <li>
              {relatorio.lidas} {c.read}
            </li>
            <li>
              {relatorio.embarques} {c.shipments}
            </li>
            <li>
              {relatorio.importadas} {c.imported}
            </li>
            <li>
              {relatorio.rejeitadas} {c.rejected}
            </li>
            <li>
              {relatorio['sem-ordem']} {c.noOrder}
            </li>
          </ul>
          {Object.entries(relatorio['por-motivo']).length ? (
            <ul>
              {Object.entries(relatorio['por-motivo']).map(([m, n]) => (
                <li key={m}>
                  {c.reasons[m] ?? m}: {n}
                </li>
              ))}
            </ul>
          ) : null}
          {relatorio.rejeicoes.length ? (
            <div className="scroll">
              <table className="cad-table">
                <thead>
                  <tr>
                    <th>{c.line}</th>
                    <th>{t.shipments.columns.nr}</th>
                    <th>{c.reason}</th>
                    <th>{c.message}</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorio.rejeicoes.slice(0, 200).map((r, i) => (
                    <tr key={i}>
                      <td>{r['num-linha']}</td>
                      <td>{r['nr-import'] ?? '—'}</td>
                      <td>{c.reasons[r.motivo] ?? r.motivo}</td>
                      <td>{r.mensagem}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
